import { getMediaServerUrl, getRandomString } from '@/utils/functions';
import {
  BoundingPosition,
  DesktopRoomObject,
  Room,
  RoomObject,
  SubRoom,
  User,
  UserRoomStateData,
} from '@/utils/models';
import { DataService } from './DataService';
import { JitsiController, UserParticipant, NoiseSuppressionEffect, JitsiTrack, JitsiLib } from './JitsiLibWrapper';
import { v4 as uuid } from 'uuid';
import EventEmitter from 'eventemitter3';
import {
  DEFAULT_ENABLE_SCREEN_SHARE_IDLING,
  DEFAULT_SCREEN_SHARE_SCOPE,
  FULL_HD_CONSTRAINTS,
  LOW_RESOLUTION_CONSTRAINTS,
} from '@/utils/constants';

declare var JitsiMeetJS: any;

export type RoomConnectionEvents = 'deviceUpdated';

/**
 * ルーム内の jitsi connection を管理するクラス。
 *
 * jitsi connectin は、subroom 単位 で作成されます。
 *
 */
export class RoomConnectionManager {
  /** イベント管理 */
  private events_ = new EventEmitter<RoomConnectionEvents>();
  onDeviceUpdated(callback: (arg: any[]) => void) {
    this.events_.on('deviceUpdated', callback);
  }
  offDeviceUpdated(callback: (arg: any[]) => void) {
    this.events_.off('deviceUpdated', callback);
  }
  off() {
    this.events_.removeAllListeners('deviceUpdated');
  }
  /** 音声・ビデオ用の接続 */
  private audioVideoConnection: JitsiController | null = null;
  /** 画面共有用のコネクション */
  readonly desktopConnections: JitsiController[] = [];
  localDesktopShareCount = 0;

  /** 音声/ビデオ用接続の状態変更イベントキュー */
  avConnectionStatusUpdateEventQueue: Partial<UpdateConnectionEventArgs>[] = [];
  private isRunningMonitor = false;
  /** queue を監視するタイマー */
  private queueMonitorTimer: any = 0;
  private lastAudioLevelNotify: number = 0;
  //#region backing fields
  //#region device ids
  /** マイクデバイスID */
  private micId: string = 'default';
  /** 音声出力(スピーカー)デバイスID */
  private speakerId: string = 'default';
  /** カメラデバイスID */
  private cameraId: string = 'default';
  //#endregion
  /** 現在の サブルーム。指定されていないときは サブルームに入っていない */
  private currentSubRoom: SubRoom | null = null;
  /** ローカルのマイクデバイスを使わないようにしているかどうか */
  private localAudioDisabled_: boolean = true;
  /** ローカルのカメラデバイスを使わないようにしているかどうか */
  private localVideoDisabled_: boolean = true;
  /** 周りに話す人が居るかどうか */
  private hasOtherSpeaker_: boolean = false;
  /** 周りに聞く人が居るかどうか */
  private hasAudience_: boolean = false;
  /** ノイズキャンセル機能が有効かどうか */
  private enableNoiseSuppressor_: boolean = false;
  /**
   * 同じ部屋に複数ユーザーいるかどうか
   */
  private hasMultipleUsersInSubRoom_: boolean = false;
  /** 現在別ウィンドウで開く処理中のトラック */
  private _currentOpeningTrack: { track: JitsiTrack; name: string } | null = null;
  connecting: boolean = false;
  private noiseSuppressionEffect: NoiseSuppressionEffect | null = null;
  /** ミュート後に audioTrack を破棄するまでの猶予時間(ms)。猶予中に解除されれば unmute だけで即復帰できる */
  private static readonly MUTED_AUDIO_TRACK_REMOVE_GRACE_MS = 10000;
  /** ミュートで audioTrack の破棄を予約した時刻(ms)。null なら予約なし */
  private audioTrackRemoveScheduledAt: number | null = null;
  /** 破棄予約の猶予時間経過後にキューの再評価を起こすためのタイマー */
  private audioTrackRemoveTimer: ReturnType<typeof setTimeout> | null = null;
  private deviceLists: MediaDeviceInfo[] = []; // default と communication の変更検知用
  //#endregion

  //#region properties
  public get localAudioDisabled(): boolean {
    return this.localAudioDisabled_;
  }
  public set localAudioDisabled(value: boolean) {
    this.avConnectionStatusUpdateEventQueue.push({ localAudioDisabled: value });
  }
  public get localVideoDisabled(): boolean {
    return this.localVideoDisabled_;
  }
  public set localVideoDisabled(value: boolean) {
    this.avConnectionStatusUpdateEventQueue.push({ localVideoDisabled: value });
  }
  public get hasOtherSpeaker(): boolean {
    return this.hasOtherSpeaker_;
  }
  public set hasOtherSpeaker(value: boolean) {
    this.avConnectionStatusUpdateEventQueue.push({ hasOtherSpeaker: value });
  }
  public get hasAudience(): boolean {
    return this.hasAudience_;
  }
  public set hasAudience(value: boolean) {
    this.avConnectionStatusUpdateEventQueue.push({ hasAudience: value });
  }
  public get enableNoiseSuppressor(): boolean {
    return this.enableNoiseSuppressor_;
  }
  public set enableNoiseSuppressor(value: boolean) {
    this.avConnectionStatusUpdateEventQueue.push({
      enableNoiseSuppressor: value,
    });
  }
  public get currentAVConnection(): JitsiController | null {
    return this.audioVideoConnection;
  }
  public get currentOpeningTrack(): { track: JitsiTrack; name: string } | null {
    return this._currentOpeningTrack;
  }
  public set currentOpeningTrack(value: { track: JitsiTrack; name: string } | null) {
    this._currentOpeningTrack = value;
  }

  /** 現在のルームの音声・動画用コネクションIdを取得します。 */
  public get currentConnectionId(): string {
    const id = this.currentSubRoom?.subid ?? this.room.id;
    return id;
  }
  get myUserRoomState() {
    return this.usersData.find((u) => u.userState.userId === this.user.id);
  }
  get screenShareScope() {
    return this.room.screenShareScope ?? DEFAULT_SCREEN_SHARE_SCOPE;
  }
  get enableScreenShareIdling() {
    return this.room.enableScreenShareIdling ?? DEFAULT_ENABLE_SCREEN_SHARE_IDLING;
  }
  get isVolumeSet() {
    return typeof this.currentSubRoom?.volume === 'number' && this.currentSubRoom?.volume > 0;
  }
  /**
   * 同じ部屋に複数ユーザーいるかどうか
   */
  get hasMultipleUsersInSubRoom() {
    const subRoomId = this.currentSubRoom?.subid ?? this.room.id;
    //自分以外で自分と同じ部屋にいるユーザーがいる場合は複数ユーザーいると判定
    return !!this.usersData.find((u) => u.userState.userId !== this.user.id && u.subRoomId === subRoomId);
  }
  //#endregion
  constructor(
    private readonly dataService: DataService,
    private readonly room: Room,
    private readonly user: User,
    private readonly usersData: UserRoomStateData[],
    private readonly shareScreens: DesktopRoomObject[],
  ) {
    this.dataService.onShareScreenStarted(this.onShareScreenStartedReceived);
    this.dataService.onShareScreenStoped(this.onShareScreenStopedReceived);
    this.dataService.onLocalUserJoinedSubRoom((objects: RoomObject[]) => {
      this.onSubRoomJoined(objects);
    });
    JitsiLib.mediaDevices.enumerateDevices((devices: Array<MediaDeviceInfo>) => {
      this.deviceLists = devices;
    });
    JitsiLib.mediaDevices.addEventListener(JitsiLib.events.mediaDevices.DEVICE_LIST_CHANGED, this.onDeviceListChanged);
    this.startQueueMonitor();
  }
  async destroy() {
    JitsiLib.mediaDevices.removeEventListener(
      JitsiLib.events.mediaDevices.DEVICE_LIST_CHANGED,
      this.onDeviceListChanged,
    );
    clearInterval(this.queueMonitorTimer);
    await Promise.all([
      ...Array.from(this.desktopConnections.values()).map(async (con) => {
        await con.destroy();
      }),
      await this.disconnectAVConnection(),
    ]);
    this.stopAllOwnShareScreens();
  }

  //#region av connection 周りの関数群
  setCurrentSubRoom(subRoom: SubRoom | null) {
    this.avConnectionStatusUpdateEventQueue.push({ subRoom });
  }
  setDevicesNames(micId: string, speakerId: string, cameraId: string) {
    this.avConnectionStatusUpdateEventQueue.push({
      micId,
      speakerId,
      cameraId,
    });
    console.log('set device names', micId, speakerId, cameraId);
  }
  private startQueueMonitor() {
    this.queueMonitorTimer = setInterval(async () => {
      if (!this.isRunningMonitor && this.avConnectionStatusUpdateEventQueue.length) {
        try {
          this.isRunningMonitor = true;
          const now = Date.now();
          const id = uuid();
          console.log('queue start monitor', id, this.avConnectionStatusUpdateEventQueue.length);
          let request: Partial<UpdateConnectionEventArgs> = { id };
          this.avConnectionStatusUpdateEventQueue.forEach((req) => Object.assign(request, req));
          this.avConnectionStatusUpdateEventQueue.splice(0, this.avConnectionStatusUpdateEventQueue.length);
          await this.doUpdateStateAudioVideoConnection(request);
          console.log('queue end monitor', id, Date.now() - now + 'ms', request);
        } catch (e) {
          console.log('error', e);
        } finally {
          this.isRunningMonitor = false;
        }
      }
    }, 200);
  }
  private async doUpdateStateAudioVideoConnection(request: Partial<UpdateConnectionEventArgs>) {
    if (typeof request.subRoom !== 'undefined' && this.currentSubRoom !== request.subRoom) {
      this.currentSubRoom = request.subRoom;
      if (this.audioVideoConnection) {
        try {
          console.log('queue disconnectAVConnection1', request.id);
          await this.disconnectAVConnection();
          console.log('queue disconnectAVConnection1 end', request.id);
        } catch (e) {
          //ignore
        }
      }
      this.stopAllOwnShareScreens();
    }

    //画面共有の自動停止タイマー操作
    if (this.enableScreenShareIdling && this.localDesktopShareCount > 0) {
      const hasMultipleUsersInSubRoomCache = this.hasMultipleUsersInSubRoom; //getter呼び出すたびにforループが走ると計算コスト高いのでキャッシュ
      const willUpdateResolutionInRoomScope =
        this.screenShareScope === 'room' && this.hasMultipleUsersInSubRoom_ !== hasMultipleUsersInSubRoomCache;
      const willUpdateResolutionInVoiceAreaScope =
        this.screenShareScope === 'voice area' &&
        typeof request.hasAudience !== 'undefined' &&
        this.hasAudience_ !== request.hasAudience;
      if (willUpdateResolutionInRoomScope || willUpdateResolutionInVoiceAreaScope) {
        const myUserRoomState = this.myUserRoomState?.userState;
        if (!myUserRoomState)
          throw new Error('自身のuserRoomStateが見つからず、画面共有タイマーの制御に失敗しました。');
        const hasViwer = this.hasScreenViwer(request.hasAudience);
        const promises: Promise<void>[] = [];
        for (const ss of this.shareScreens) {
          const onResumeScreen = () => {
            const trackUpdates = this.updateOwnShareScreenResolution(ss, hasViwer);
            trackUpdates && promises.push(...trackUpdates);
          };
          ss.updateIdleTimer(myUserRoomState, this.currentSubRoom?.volume, hasViwer, onResumeScreen);
        }
        await Promise.all(promises);
      }
      if (this.screenShareScope === 'room') this.hasMultipleUsersInSubRoom_ = hasMultipleUsersInSubRoomCache;
    }

    if (typeof request.localAudioDisabled !== 'undefined') {
      this.localAudioDisabled_ = request.localAudioDisabled;
    }
    if (typeof request.localVideoDisabled !== 'undefined') {
      this.localVideoDisabled_ = request.localVideoDisabled;
    }
    if (typeof request.hasOtherSpeaker !== 'undefined') {
      this.hasOtherSpeaker_ = request.hasOtherSpeaker;
    }
    if (typeof request.hasAudience !== 'undefined') {
      this.hasAudience_ = request.hasAudience;
    }
    let enableNoiseSuppressorChanged = false;
    if (
      typeof request.enableNoiseSuppressor === 'boolean' &&
      this.enableNoiseSuppressor_ !== request.enableNoiseSuppressor
    ) {
      this.enableNoiseSuppressor_ = request.enableNoiseSuppressor;
      enableNoiseSuppressorChanged = true;
    }
    let micIdUpdated = false;
    let cameraIdUpdated = false;
    if (typeof request.speakerId !== 'undefined' || request.micId !== 'undefined' || request.cameraId !== 'undefined') {
      this.speakerId = request.speakerId || this.speakerId;
      if (typeof request.micId !== 'undefined' && this.micId !== request.micId) {
        this.micId = request.micId;
        micIdUpdated = true;
      }
      if (typeof request.cameraId !== 'undefined' && this.cameraId !== request.cameraId) {
        this.cameraId = request.cameraId;
        cameraIdUpdated = true;
      }
    }

    if (this.hasOtherSpeaker || (this.hasAudience && (!this.localAudioDisabled || !this.localVideoDisabled))) {
      // 近くに音声もしくはビデオをonにしている誰かがいる場合 hasOtherSpeaker
      // 聴き手が居て hasAudience、自分が音声もしくはビデオをonにしている場合
      const jitsiAudioConferenceId = 'av-' + this.currentConnectionId;

      if (!this.audioVideoConnection) {
        this.connecting = true;
        this.audioVideoConnection = new JitsiController(getMediaServerUrl(), jitsiAudioConferenceId, this.user.id);
        this.audioVideoConnection.onJoinUser(this.onJoinUserHandler);
        this.audioVideoConnection.on('disconnected', this.onDisconnectHandler);
      }
      const connection = this.audioVideoConnection;
      if (connection.status === 'disconnected' || connection.status === 'error') {
        console.log('queue joinConrefence', request.id);
        await connection.joinConrefence(this.user.name);
        connection.conference?.addEventListener(JitsiMeetJS.events.conference.TRACK_REMOVED, (track: JitsiTrack) => {
          if (track.isLocal() && track.getType() === 'audio') {
            // effect の停止(stopEffect)は lib-jitsi-meet の track.dispose() が行うため、ここでは参照を外すだけ
            this.noiseSuppressionEffect = null;
          }
        });
      }
      if (micIdUpdated && connection.hasAudioTrack) {
        console.log('queue removeAudioTrack', request.id);
        this.cancelScheduledAudioTrackRemoval();
        await connection.removeAudioTrack();
      }
      if (cameraIdUpdated && connection.hasVideoTrack) {
        console.log('queue removeVideoTrack', request.id);
        await connection.removeVideoTrack();
      }
      connection.setDevicesNames(this.micId, this.speakerId, this.cameraId);

      if (!this.localAudioDisabled && !connection.hasAudioTrack) {
        this.connecting = true;
        console.log('queue createAndAddAudioTrack', request.id);
        let audioTrack = await connection.createAndAddAudioTrack();
        audioTrack.addEventListener(JitsiMeetJS.events.track.TRACK_AUDIO_LEVEL_CHANGED, (newAudioLevel) => {
          // ミュート中(トラック破棄の猶予中を含む)は音声レベルを通知しない
          if (!this.localAudioDisabled && newAudioLevel > 0.1 && this.lastAudioLevelNotify < Date.now() - 800) {
            // 800 ms 以上経過してない場合は通知しない
            this.lastAudioLevelNotify = Date.now();
            this.dataService.emitAudioLevel(newAudioLevel);
          }
        });
        if (this.enableNoiseSuppressor) {
          this.noiseSuppressionEffect = new NoiseSuppressionEffect(getMediaServerUrl());
          await audioTrack.setEffect(this.noiseSuppressionEffect);
        }
      } else if (this.localAudioDisabled && connection.hasAudioTrack) {
        // ミュートは mute() で即時反映し、トラック破棄(キャプチャ解放=マイク使用中インジケーター消灯)は
        // 猶予時間後に行う。猶予中に解除されれば unmute だけで復帰でき、effect の再構築を避けられる
        const audioTrack = connection.audioTrack;
        if (!audioTrack?.isMuted()) {
          console.log('queue muteAudioTrack', request.id);
          await audioTrack?.mute();
        }
        if (
          this.audioTrackRemoveScheduledAt !== null &&
          Date.now() - this.audioTrackRemoveScheduledAt >= RoomConnectionManager.MUTED_AUDIO_TRACK_REMOVE_GRACE_MS
        ) {
          console.log('queue removeAudioTrack', request.id);
          this.cancelScheduledAudioTrackRemoval();
          await connection.removeAudioTrack();
        } else {
          if (this.audioTrackRemoveScheduledAt === null) {
            this.audioTrackRemoveScheduledAt = Date.now();
          }
          if (this.audioTrackRemoveTimer === null) {
            // 猶予時間経過後にキューを再評価させる(実際の破棄判定は上の分岐で行う)。
            // タイマー発火が猶予時間より僅かに早くても判定できるよう、残時間+マージンで張り直す
            const remainMs =
              this.audioTrackRemoveScheduledAt + RoomConnectionManager.MUTED_AUDIO_TRACK_REMOVE_GRACE_MS - Date.now();
            this.audioTrackRemoveTimer = setTimeout(() => {
              this.audioTrackRemoveTimer = null;
              this.avConnectionStatusUpdateEventQueue.push({});
            }, Math.max(remainMs, 0) + 50);
          }
          if (enableNoiseSuppressorChanged) {
            await this.applyNoiseSuppressorSetting(audioTrack);
          }
        }
      } else if (connection.hasAudioTrack) {
        // マイク使用中: 破棄予約が残っていれば取り消し、猶予中のミュートなら解除する
        this.cancelScheduledAudioTrackRemoval();
        const audioTrack = connection.audioTrack;
        if (audioTrack?.isMuted()) {
          console.log('queue unmuteAudioTrack', request.id);
          await audioTrack.unmute();
        }
        if (enableNoiseSuppressorChanged) {
          await this.applyNoiseSuppressorSetting(audioTrack);
        }
      }

      if (!this.localVideoDisabled && !connection.hasVideoTrack) {
        this.connecting = true;
        console.log('queue createAndAddVideoTrack', request.id);
        await connection.createAndAddVideoTrack();
      } else if (this.localVideoDisabled && connection.hasVideoTrack) {
        console.log('queue removeVideoTrack', request.id);
        await connection.removeVideoTrack();
      }
    } else {
      console.log('queue disconnectAVConnection2', request.id);
      await this.disconnectAVConnection();
      console.log('queue disconnectAVConnection2 end', request.id);
    }
    this.connecting = false;
  }
  private async disconnectAVConnection() {
    this.cancelScheduledAudioTrackRemoval();
    if (this.audioVideoConnection) {
      await this.audioVideoConnection.destroy();
      this.audioVideoConnection = null;
    }
  }
  /** ミュート時の audioTrack 破棄予約を取り消す */
  private cancelScheduledAudioTrackRemoval() {
    if (this.audioTrackRemoveTimer !== null) {
      clearTimeout(this.audioTrackRemoveTimer);
      this.audioTrackRemoveTimer = null;
    }
    this.audioTrackRemoveScheduledAt = null;
  }
  /** 現在の enableNoiseSuppressor 設定を audioTrack に適用する */
  private async applyNoiseSuppressorSetting(audioTrack: JitsiTrack | null) {
    if (this.enableNoiseSuppressor) {
      this.noiseSuppressionEffect = new NoiseSuppressionEffect(getMediaServerUrl());
      await audioTrack?.setEffect(this.noiseSuppressionEffect);
    } else {
      await audioTrack?.setEffect(undefined);
      this.noiseSuppressionEffect = null;
    }
  }
  private onJoinUserHandler = (user?: UserParticipant) => {
    if (user) {
      const target = this.usersData.find((u) => u.userState.userId === user.userId);
      if (target) {
        target.userParticipant = user;
        target.applyVolume();
      }
    }
  };
  private onDisconnectHandler = () => {
    setTimeout(() => {
      this.avConnectionStatusUpdateEventQueue.push({});
    }, 2000);
  };
  private onDeviceListChanged = (devices: MediaDeviceInfo[]) => {
    const { micId, speakerId, cameraId } = this;
    const defaultDeviceId = 'default';
    let isChange = false;
    if (micId === defaultDeviceId) {
      const nd = devices.find((d) => d.deviceId === defaultDeviceId && d.kind === 'audioinput');
      const od = this.deviceLists.find((d) => d.deviceId === defaultDeviceId && d.kind === 'audioinput');
      if (nd?.label !== od?.label) {
        this.micId = '';
        isChange = true;
      }
    }
    if (speakerId === defaultDeviceId) {
      const nd = devices.find((d) => d.deviceId === defaultDeviceId && d.kind === 'audiooutput');
      const od = this.deviceLists.find((d) => d.deviceId === defaultDeviceId && d.kind === 'audiooutput');
      if (nd?.label !== od?.label) {
        this.speakerId = '';
        isChange = true;
      }
    }
    this.setDevicesNames(micId, speakerId, cameraId);
    this.deviceLists = devices;
    if (isChange) {
      this.events_.emit('deviceUpdated', devices);
    }
  };
  //#endregion

  //#region 画面共有周りの関数群
  startShareScreen() {
    // 常に新しいコネクションを使うように変更
    const roomid = this.currentConnectionId;
    const ssConfId = `ss-${roomid}-${getRandomString(4)}`;
    const conn = this.createOrGetDesctopController(ssConfId);
    this.dataService.emitStartShareScreen(this.currentConnectionId, conn.conferenceId);
    const connection = conn;
    connection
      .createAndAddDesktopTrack()
      .then(() => {
        this.updateLocalDesktopShareCount();
      })
      .catch((error) => {
        console.log('canceled..', error);
        this.stopScreenShare(connection.conferenceId);
      });
  }
  stopScreenShareByRoomObject(object: DesktopRoomObject) {
    this.stopScreenShare(object.props.channelId);
  }
  private stopAllOwnShareScreens() {
    const myss = this.shareScreens.filter((ss) => ss.createdUserId === this.user.id);
    myss.forEach((ss) => this.stopScreenShareByRoomObject(ss));
  }
  private stopScreenShare(confId: string) {
    let conn = this.desktopConnections.find((c) => c.conferenceId === confId);
    if (conn && conn.hasDesktopTrack) {
      conn.removeDesktopTrack().then(() => {});
    }
    this.dataService.emitStopShareScreen(confId);
    this.updateLocalDesktopShareCount();
  }
  private updateLocalDesktopShareCount() {
    this.localDesktopShareCount = this.desktopConnections.filter((c) => c.hasDesktopTrack).length;
  }
  private createOrGetDesctopController(confId: string): JitsiController {
    let controller = this.desktopConnections.find((c) => c.conferenceId === confId);
    if (!controller) {
      controller = new JitsiController(getMediaServerUrl(), confId, this.user.id);
      this.desktopConnections.push(controller);
      const con = controller;
      con.on('tracksUpdate', (track) => {
        this.updateAllOwnShareScreensResolution();
        this.shareScreens
          .filter((v) => !v.createdUserParticipant)
          .forEach((v) => {
            let user = con.participantsUserMap[v.createdUserId];
            if (user?.desktopTrack) {
              v.createdUserParticipant = user;
            }
          });
      });
      con.on('tracksRemoved', (track) => {
        console.log('desktop conn tracks removed called', con.conferenceId, con.tracks.length);
        setTimeout(() => {
          // なぜか remove を呼ばれるっぽいので遅延する
          if (!con.tracks.length) {
            let index = this.desktopConnections.indexOf(con);
            if (index >= 0) {
              this.desktopConnections.splice(index, 1);
            }
            con.destroy();
          }
        }, 1000);
      });
      const cid = confId;
      con.on('desktopEnded', () => {
        this.stopScreenShare(cid);
      });
      con.joinConrefence(this.user.name);
    }
    return controller;
  }
  /**
   * 全ての自分の画面共有の解像度を変更します。
   */
  private async updateAllOwnShareScreensResolution() {
    const hasViwer = this.hasScreenViwer();
    if (!this.enableScreenShareIdling) return; //自動停止が無効な時は解像度変更を行う必要がないので弾く
    const localDesktopObjects = this.shareScreens.filter((ss) => ss.createdUserId === this.user.id);
    if (!localDesktopObjects.length) return;
    const promises = localDesktopObjects
      .map((ss) => {
        return this.updateOwnShareScreenResolution(ss, hasViwer);
      })
      .flat();
    await Promise.all(promises);
  }

  /**
   * 任意の自分の画面共有の解像度を変更します。
   * @param ss
   * @param hasViwer
   * @returns
   */
  private updateOwnShareScreenResolution(ss: DesktopRoomObject, hasViwer?: boolean) {
    if (ss.createdUserId !== this.user.id) return;
    if (typeof hasViwer === 'undefined') hasViwer = this.hasScreenViwer();
    const constraints: MediaTrackConstraints =
      !ss.isIdle || hasViwer ? FULL_HD_CONSTRAINTS : LOW_RESOLUTION_CONSTRAINTS;
    return ss.controller?.desktopTracks?.map((t) => t.track.applyConstraints(constraints));
  }

  private hasScreenViwer(hasAudience = this.hasAudience_) {
    if (!this.enableScreenShareIdling) {
      return true;
    }
    return this.screenShareScope === 'room' || this.isVolumeSet ? this.hasMultipleUsersInSubRoom : hasAudience;
  }

  // private handle

  //#region share screen event handlers
  private onShareScreenStartedReceived = (payload: RoomObject, retryCount: number = 0) => {
    if (this.shareScreens.find((s) => s.id === payload.id)) {
      // 多重登録なので無視
      return;
    }
    const confId = payload.props.channelId;
    // 遅延されている可能性があるので適用後のIDを取得
    let request: Partial<UpdateConnectionEventArgs> = {};
    this.avConnectionStatusUpdateEventQueue.forEach((req) => Object.assign(request, req));
    const roomid =
      typeof request.subRoom !== 'undefined' ? request.subRoom?.subid ?? this.room.id : this.currentConnectionId;
    if (!confId.startsWith(`ss-${roomid}`)) {
      console.log('取得した接続が異なるIDだったので画面共有を開始できませんでした。', confId, roomid, retryCount);
      if (retryCount < 5) {
        setTimeout(() => {
          this.onShareScreenStartedReceived(payload, retryCount++);
        }, 200);
      }
      return;
    }
    const controller = this.createOrGetDesctopController(confId);
    let up = controller.participantsUserMap[payload.createdUserId] ?? null;
    const createdUserRoomState = this.usersData.find((u) => u.userState.userId === payload.createdUserId);
    if (!this.myUserRoomState) {
      //たぶんありえない
      console.error('自分のUserRoomStateが見つからなかったため画面共有開始処理を中断します。');
      return;
    }
    if (!createdUserRoomState) {
      //たぶんありえない
      console.error('画面共有者のUserRoomStateが見つからなかったため画面共有開始処理を中断します。');
      return;
    }
    if (up) {
      let ss: DesktopRoomObject = new DesktopRoomObject(
        payload,
        up,
        createdUserRoomState,
        this.screenShareScope,
        this.enableScreenShareIdling,
        false,
        controller,
      );
      this.shareScreens.push(ss);
      ss.updateVisualStates(this.myUserRoomState.userState, this.currentSubRoom?.volume);
      ss.registerIdleEventHandler(this.onShareScreenIdle.bind(this));
      ss.updateIdleTimer(this.myUserRoomState.userState, this.currentSubRoom?.volume, this.hasScreenViwer());
    } else {
      // join よりも前に受信してしまっているので、遅延する
      const handler = (user?: UserParticipant) => {
        if (payload.createdUserId === user?.userId) {
          let ss: DesktopRoomObject = new DesktopRoomObject(
            payload,
            user,
            createdUserRoomState,
            this.screenShareScope,
            this.enableScreenShareIdling,
            false,
            controller,
          );
          this.shareScreens.push(ss);
          ss.updateVisualStates(this.myUserRoomState!.userState, this.currentSubRoom?.volume);
          ss.registerIdleEventHandler(this.onShareScreenIdle.bind(this));
          ss.updateIdleTimer(this.myUserRoomState!.userState, this.currentSubRoom?.volume, this.hasScreenViwer());
          console.log('join よりも前に受信してしまっているので、遅延する');
          controller.offJoinUser(handler);
        }
      };
      controller.onJoinUser(handler);
    }
    this.updateLocalDesktopShareCount();
  };

  private onShareScreenStopedReceived = (obj: RoomObject) => {
    const index = this.shareScreens.findIndex((o) => o.id === obj.id);
    if (index >= 0) {
      const ss = this.shareScreens.splice(index, 1);
      if (ss?.length && ss[0].createdUserParticipant?.desktopTrack) {
        const { el, track } = ss[0].createdUserParticipant.desktopTrack;
        if (el && track) {
          track.detach(el);
          el.pause();
        }
      }
    }
  };

  private async onShareScreenIdle(ss: DesktopRoomObject) {
    if (!this.myUserRoomState) {
      //たぶんありえない
      console.error('画面共有の自動停止中に自分のUserRoomStateが見つからなかったため、処理を中断します。');
      return;
    }
    //画面共有オブジェクトの可視状態の更新と、自分の画面共有オブジェクトの場合は解像度の更新を行う。
    ss.updateVisualStates(this.myUserRoomState.userState, this.currentSubRoom?.volume);
    await Promise.all(this.updateOwnShareScreenResolution(ss, this.hasScreenViwer()) ?? []);
  }
  //#endregion
  //#endregion

  //#region data service event handlers
  private onSubRoomJoined(objects: RoomObject[]) {
    this.shareScreens.splice(0, this.shareScreens.length);
    for (let con of this.desktopConnections) {
      con.destroy();
    }
    this.desktopConnections.splice(0, this.desktopConnections.length);
    objects.filter((o) => o.objectType === 'share screen').forEach((o) => this.onShareScreenStartedReceived(o));
  }
  //#endregion
}

export interface UpdateConnectionEventArgs {
  id: string;
  /** サブルーム情報 */
  subRoom: SubRoom | null;
  /** ローカルのマイクデバイスを使わないようにしているかどうか */
  localAudioDisabled: boolean;
  /** ローカルのカメラデバイスを使わないようにしているかどうか */
  localVideoDisabled: boolean;
  /** 周りに話す人が居るかどうか */
  hasOtherSpeaker: boolean;
  /** 周りに聞く人が居るかどうか */
  hasAudience: boolean;
  /** マイクデバイスID */
  micId: string;
  /** 音声出力(スピーカー)デバイスID */
  speakerId: string;
  /** カメラデバイスID */
  cameraId: string;
  /** ノイズキャンセル */
  enableNoiseSuppressor: boolean;
}
